import { DatePipe } from '@angular/common';
import {
  Component,
  computed,
  inject,
  input,
  output,
  signal,
  ChangeDetectionStrategy,
  OnInit,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { PageEvent } from '@angular/material/paginator';
import { FilterGroup, TableFilterBarComponent } from '@custom-components/general/table-filter-bar';
import { TableComponent } from '@custom-components/general/table/table.component';
import { ACTIVITY_TABLE_COLUMNS } from '@core/constants';
import { TagStateOf } from '@core/helpers';
import {
  ACTIVITY_STATUSES,
  activityStatusLabel,
  deviceActionLabel,
  deviceActivitySummary,
} from '@core/helpers/activity-label.helper';
import {
  ActivityDateRange,
  ActivityDateRangeComponent,
} from '@app/pages/devices/components/activity-date-range/activity-date-range.component';
import { ActivityMessageDialogComponent } from '@app/pages/devices/components/activity-message-dialog/activity-message-dialog.component';
import { DeviceVerificationDetailsDialogComponent } from '@app/pages/devices/components/device-verification-dialog/device-verification-details-dialog.component';
import { DeviceConfigFileDetailsDialogComponent } from '@app/pages/settings/containers/device-config-files/device-config-file-details-dialog/device-config-file-details-dialog.component';
import { InitialConfigFileDetailsDialogComponent } from '@app/pages/settings/containers/initial-config-files/initial-config-file-details-dialog/initial-config-file-details-dialog.component';
import { Activity, ActivityFilters, ActivityResponse } from '@core/models/activity.model';
import { VerificationAttempt } from '@core/models/verification.model';
import { TranslateModule, TranslatePipe, TranslateService } from '@ngx-translate/core';

/** The `action` key the configuration tab's verification card deep-links this tab with (see
 * DeviceDetailComponent.onViewAllVerifications) — one of FILTERABLE_ACTIONS below. */
export const VERIFICATION_ACTION = 'verification';

export const FILTERABLE_ACTIONS = [
  'sim_connection_test',
  'lan_connection_test',
  'pol',
  'send_initial_config',
  'send_device_config',
  'verification',
  'clear_device_config',
  'firmware_update',
  'sim_topup',
  'sim_extension',
] as const;

@Component({
  selector: 'xemex-device-activity-tab',
  imports: [
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    ActivityDateRangeComponent,
    TableComponent,
    TableFilterBarComponent,
    TranslateModule,
  ],
  providers: [TranslatePipe, DatePipe],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './device-activity-tab.component.html',
})
export class DeviceActivityTabComponent implements OnInit {
  activityRes = input<ActivityResponse>();
  isLoading = input<boolean>(false);
  isXemex = input<boolean>(false);
  /** Raw action key(s) to pre-apply, comma-joined exactly as `ActivityFilters.action` carries
   * them — `verification` when deep-linked from the configuration tab's verification card. The
   * parent issues the filtered request itself; this only mirrors it in the filter bar, so the
   * chips show and a later filter edit keeps the actions. */
  readonly initialAction = input<string>();

  pageChange = output<{ pageIndex: number; pageSize: number }>();
  filterChange = output<ActivityFilters>();

  readonly columns = ACTIVITY_TABLE_COLUMNS;

  currentPage = 1;
  pageSize = 10;

  actionLabelToKey = new Map<string, string>();
  statusLabelToKey = new Map<string, string>();

  private appliedFilters = signal<ActivityFilters>({});
  private readonly dialog = inject(MatDialog);
  private readonly datePipe = inject(DatePipe);
  private readonly translate = inject(TranslateService);

  constructor() {
    FILTERABLE_ACTIONS.forEach(key => {
      const label = this.translate.instant('devices.detailTabs.activity.actions.' + key);
      this.actionLabelToKey.set(label, key);
    });
    ACTIVITY_STATUSES.forEach(raw => {
      this.statusLabelToKey.set(this.translateStatus(raw), raw);
    });
  }

  /** The filter bar keys its own state on the translated option labels, not on the raw action
   * keys — see onFilterChange, which maps them back. */
  readonly initialFilters = computed<Record<string, string[]>>(() => {
    const actions = (this.initialAction() ?? '').split(',').filter(Boolean);
    const filters: Record<string, string[]> = {};
    if (actions.length) filters['action'] = actions.map(a => this.translateAction(a));
    return filters;
  });

  ngOnInit(): void {
    const action = this.initialAction();
    if (action) this.appliedFilters.set({ action });
  }

  readonly filterGroups = computed<FilterGroup[]>(() => [
    {
      key: 'action',
      label: this.translate.instant('devices.detailTabs.activity.filter.action'),
      options: FILTERABLE_ACTIONS.map(k =>
        this.translate.instant('devices.detailTabs.activity.actions.' + k),
      ),
    },
    {
      key: 'status',
      label: this.translate.instant('devices.detailTabs.activity.filter.status'),
      options: ACTIVITY_STATUSES.map(s => this.translateStatus(s)),
    },
  ]);

  readonly tableData = computed<Record<string, unknown>[]>(() =>
    (this.activityRes()?.activities ?? []).map(a => this.mapToTableRow(a)),
  );

  readonly totalCount = computed(() => this.activityRes()?.numberOfItems);

  mapToTableRow(item: Activity): Record<string, unknown> {
    return {
      action: this.translateAction(item.action),
      channel_number: item.channel_number ?? '–',
      message: this.buildMessageSummary(item),
      status: this.translateStatus(item.status),
      statusState: TagStateOf.activityStatus(item.status),
      initiated_by: item.initiated_by,
      initiated_on: this.datePipe.transform(item.initiated_on, 'd MMM yyyy, HH:mm') ?? '',
      _activity: item,
      _hasMessage: !!item.message,
      _isRowClickable: this.isRowClickable(item),
      _actionIcon: item.action === 'verification' ? 'manage_search' : 'description',
    };
  }

  buildMessageSummary(item: Activity): string {
    return deviceActivitySummary(this.translate, item);
  }

  isRowClickable(activity: Activity): boolean {
    if (activity.action === 'verification' && !!activity.correlation_id) return true;
    if (!this.isXemex()) return false;
    return (
      (!!activity.initial_file_id && !!activity.initial_version) ||
      (!!activity.device_file_id && !!activity.device_version)
    );
  }

  onSearchChange(query: string): void {
    this.currentPage = 1;
    this.appliedFilters.set({ ...this.appliedFilters(), initiatedBy: query || undefined });
    this.filterChange.emit(this.appliedFilters());
  }

  onFilterChange(filters: Record<string, string[]>): void {
    const actionLabels = filters['action'] ?? [];
    const statusLabels = filters['status'] ?? [];
    const actionKeys = actionLabels.map(l => this.actionLabelToKey.get(l) ?? l);
    const statusValues = statusLabels.map(l => this.statusLabelToKey.get(l) ?? l);
    this.currentPage = 1;
    this.appliedFilters.set({
      ...this.appliedFilters(),
      action: actionKeys.length ? actionKeys.join(',') : undefined,
      status: statusValues.length ? statusValues.join(',') : undefined,
    });
    this.filterChange.emit(this.appliedFilters());
  }

  onDateRangeChange(range: ActivityDateRange): void {
    const { initiatedOnFrom: _f, initiatedOnTo: _t, ...rest } = this.appliedFilters();
    this.currentPage = 1;
    this.appliedFilters.set({ ...rest, ...range });
    this.filterChange.emit(this.appliedFilters());
  }

  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex + 1;
    this.pageChange.emit({ pageIndex: event.pageIndex, pageSize: this.pageSize });
  }

  openActionDialog(row: Record<string, unknown>): void {
    const activity = row['_activity'] as Activity;
    if (!this.isRowClickable(activity)) return;

    if (activity.action === 'verification' && activity.correlation_id) {
      this.dialog.open(DeviceVerificationDetailsDialogComponent, {
        width: '850px',
        maxWidth: '850px',
        height: '80vh',
        data: {
          verificationId: activity.correlation_id,
          initiatedBy: activity.initiated_by,
          initiatedOn: activity.initiated_on,
          status: activity.status,
          error: null,
        } as VerificationAttempt,
      });
      return;
    }

    if (activity.initial_file_id && activity.initial_version) {
      this.dialog.open(InitialConfigFileDetailsDialogComponent, {
        width: '850px',
        maxWidth: '850px',
        maxHeight: '90vh',
        panelClass: 'h-full',
        data: { fileId: activity.initial_file_id, versionId: activity.initial_version },
      });
    } else if (activity.device_file_id && activity.device_version) {
      this.dialog.open(DeviceConfigFileDetailsDialogComponent, {
        width: '850px',
        maxWidth: '850px',
        maxHeight: '90vh',
        panelClass: 'h-full',
        data: { fileId: activity.device_file_id, versionId: activity.device_version },
      });
    }
  }

  openMessageDialog(row: Record<string, unknown>): void {
    const activity = row['_activity'] as Activity;
    if (!activity.message) return;
    this.dialog.open(ActivityMessageDialogComponent, {
      data: { activity },
      autoFocus: false,
    });
  }

  translateStatus(status: string): string {
    return activityStatusLabel(this.translate, status);
  }

  translateAction(action: string): string {
    return deviceActionLabel(this.translate, action);
  }
}
